import { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { DashboardQueryProvider } from './features/dashboard/DashboardQueryProvider';
import { DuskTheme } from './features/dashboard/DuskTheme';
import { useSwUpdate } from './features/sw-update/useSwUpdate';
import { useSystemReloadWS } from './features/sw-update/useSystemReloadWS';
import { useCalendarWS } from './features/calendar/useCalendarWS';
import { usePointsWS } from './features/points/usePointsWS';
import { useFeedWS } from './features/feed/useFeedWS';
import { useSmartHomeWS } from './features/dashboard/useSmartHomeWS';
import { SwUpdateBanner } from './features/sw-update/SwUpdateBanner';
import { SwIdleReload } from './features/sw-update/SwIdleReload';
import { registerShoppingDrainTriggers } from './lib/sw-message';
import {
  AdminSettingsRoute,
  DashboardRoute,
  InstallRoute,
  LoginRoute,
  NotFoundRoute,
  OnboardingRoute,
  SettingsRoute,
} from './routes';
import { PoolBoardRoute } from './features/chores/PoolBoardRoute';
import { HomeworkRoute } from './features/chores/HomeworkRoute';
import { CalendarRoute } from './features/calendar/CalendarRoute';
import { ShoppingRoute } from './features/shopping/ShoppingRoute';
import { AssistantRoute } from './features/assistant/AssistantRoute';
import { TodayRoute } from './features/today/TodayRoute';
import { OverviewRoute } from './features/dashboard/OverviewRoute';
import { ApprovalsRoute } from './features/approvals/ApprovalsRoute';
import { ParentConsoleRoute } from './features/parents/ParentConsoleRoute';
import { FeedRoute } from './features/feed/FeedRoute';
import { WalletRoute } from './features/points/WalletRoute';
import { RewardsStoreRoute } from './features/rewards/RewardsStoreRoute';
import { ShopRoute } from './features/shop/ShopRoute';
import { MenyRoute } from './features/meals/MenyRoute';
import { AchievementGalleryRoute } from './features/achievements/AchievementGalleryRoute';
import { LeaderboardRoute } from './features/leaderboard/LeaderboardRoute';
import { MemorySettingsRoute } from './features/settings/MemorySettingsRoute';
import { CelebrationProvider } from './features/celebrations/CelebrationProvider';
import { SubTabsLayout } from './features/dashboard/SubTabsLayout';
import { RedirectKeepingSearch } from './components/RedirectKeepingSearch';
import { ToastViewport } from './features/toast/ToastViewport';
import { MemberInviteRoute } from './features/onboarding/MemberInviteRoute';
import { PasswordResetRoute } from './features/login/PasswordResetRoute';
import { SystemLoginRoute } from './features/system/SystemLoginRoute';
import { SystemShellRoute } from './features/system/SystemShellRoute';
import { SYSTEM_HOME_PATH, SYSTEM_LOGIN_PATH } from './features/system/systemApi';
import {
  CHOOSE_PASSWORD_PATH,
  ChooseOwnPasswordRoute,
  RequireOwnPassword,
} from './features/auth/ChooseOwnPasswordRoute';
// Plan 06-02 D-15: render-error containment. RouteErrorBoundary attaches
// to every authenticated route via the RR7 ErrorBoundary route property;
// AppErrorBoundary wraps RouterProvider at the App root so an uncaught
// render crash anywhere in the tree (including DashboardQueryProvider
// / CelebrationProvider mount) shows <ErrorState variant="page"> instead
// of React's white screen of death.
import { AppErrorBoundary, RouteErrorBoundary } from './components/error-boundary';

// Test-only window hooks (W9, Plan 01-15 Task 1). Self-gated on
// `import.meta.env.MODE === 'test'` so prod bundles tree-shake to a no-op.
import './dev/test-hooks';

/**
 * React Router v7 SPA shell — Phase 1 route table.
 *
 * Declared routes cover every Phase 1 surface; feature plans replace the
 * stubs in `./routes.tsx`. `/onboarding` redirects to step-1 so any entry
 * into the wizard lands on the welcome form. The wildcard at the tail
 * renders the 404 stub — no downstream plan replaces this one.
 *
 * Plan 01-15 mounts (FND-02 service-worker update UX):
 *   - `useSwUpdate()`       — registers SW, wires onNeedRefresh into store
 *   - `useSystemReloadWS()` — opens /ws, listens for admin force-reload
 *   - `<SwUpdateBanner />`  — phone/desktop bottom banner overlay
 *   - `<SwIdleReload />`    — tablet idle-fade auto-reload overlay
 */
// Rule 1 deviation (Plan 11): React Router v7's compilePath only splits
// `/:name` where the colon follows the slash, so `step-:n` does NOT match
// `step-1` — the earlier stub was never route-tested.  Use a whole-segment
// `:step` param and parse "step-<N>" inside OnboardingWizard.
// Plan 06-02 Task 1: every authenticated route declares
// `ErrorBoundary: RouteErrorBoundary` so a loader/render crash falls back
// to a localised <ErrorState variant="page"> instead of React's white
// screen. The `/onboarding` Navigate redirect inherits whatever its
// parent renders — RR7 still accepts the property on a Navigate element
// for forward-compat with future loader-bearing redirects.
// Testgalleriene under /dev/ (#152). Vitest kjører i MODE 'test'; e2e-bygget er
// et produksjonsbygg og slår dem på med VITE_DEV_GALLERY=1 (playwright.config.ts),
// fordi spec-ene ellers møter en 404. Begge er byggetidskonstanter, så et
// vanlig produksjonsbygg mister rutene helt.
const DEV_GALLERY = import.meta.env.MODE === 'test' || import.meta.env.VITE_DEV_GALLERY === '1';

const router = createBrowserRouter([
  // v1.1 nav fix: DashboardRoute is now the persistent app shell. It renders
  // the IdleMode hub overlay + InteractiveMode (which owns the NavShell and an
  // <Outlet/>), so every in-app surface below keeps the navigation chrome
  // instead of rendering as a chromeless standalone page. The index route is
  // the overview "Hjem" — an at-a-glance, tappable version of the idle hub
  // (weather, today's events, the chore pool, home status) with the nav
  // visible; the chore pool opens from its card and also lives at
  // /oppgavebunken. Onboarding / login / installer stay OUTSIDE the shell
  // (no nav, no idle) as full-screen flows.
  {
    path: '/',
    // #143: et medlem med arvet husstandspassord sendes til /velg-passord
    // før noe annet i appen kan brukes.
    element: (
      <RequireOwnPassword>
        <DashboardRoute />
      </RequireOwnPassword>
    ),
    ErrorBoundary: RouteErrorBoundary,
    // Plan 06-02 Task 1: every authenticated surface keeps its own
    // RouteErrorBoundary so a loader/render crash falls back to a localised
    // <ErrorState variant="page"> inside the shell (nav stays usable) rather
    // than taking down the whole tree.
    children: [
      { index: true, element: <OverviewRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: 'i-dag', element: <TodayRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: 'oppgavebunken', element: <PoolBoardRoute />, ErrorBoundary: RouteErrorBoundary },
      // #153: push ved godkjent/avvist oppgave lenker hit (chores.subscribers.ts)
      // og ga 404. Å åpne akkurat den oppgaven er ny oppførsel 10.
      { path: 'oppgaver', element: <RedirectKeepingSearch to="/oppgavebunken" /> },
      { path: 'lekser', element: <HomeworkRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: 'familie', element: <FeedRoute />, ErrorBoundary: RouteErrorBoundary },
      // #153: Plan-fanen — Kalender · Middag · Handle som underfaner.
      {
        element: <SubTabsLayout group="plan" />,
        children: [
          { path: 'kalender', element: <CalendarRoute />, ErrorBoundary: RouteErrorBoundary },
          { path: 'meny', element: <MenyRoute />, ErrorBoundary: RouteErrorBoundary },
          { path: 'handleliste', element: <ShoppingRoute />, ErrorBoundary: RouteErrorBoundary },
        ],
      },
      { path: 'assistent', element: <AssistantRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: 'godkjenninger', element: <ApprovalsRoute />, ErrorBoundary: RouteErrorBoundary },
      // The parent console. /godkjenninger stays mounted alongside it because
      // chore-completed pushes and both SLA escalations deep-link there.
      { path: 'foreldre', element: <ParentConsoleRoute />, ErrorBoundary: RouteErrorBoundary },
      // #153: Premier-fanen — Belønninger · Butikk · Merker · Tavle som underfaner.
      {
        element: <SubTabsLayout group="rewards" />,
        children: [
          { path: 'belonninger', element: <RewardsStoreRoute />, ErrorBoundary: RouteErrorBoundary },
          { path: 'butikk', element: <ShopRoute />, ErrorBoundary: RouteErrorBoundary },
          {
            path: 'utmerkelser',
            element: <AchievementGalleryRoute />,
            ErrorBoundary: RouteErrorBoundary,
          },
          { path: 'leaderboard', element: <LeaderboardRoute />, ErrorBoundary: RouteErrorBoundary },
        ],
      },
      { path: 'poeng', element: <WalletRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: ':memberId/poeng', element: <WalletRoute />, ErrorBoundary: RouteErrorBoundary },
      {
        path: ':memberId/utmerkelser',
        element: <AchievementGalleryRoute />,
        ErrorBoundary: RouteErrorBoundary,
      },
      { path: 'settings', element: <SettingsRoute />, ErrorBoundary: RouteErrorBoundary },
      { path: 'settings/admin', element: <AdminSettingsRoute />, ErrorBoundary: RouteErrorBoundary },
      // AI-21 (#193): «Hva Stell husker», for alle roller.
      { path: 'settings/hukommelse', element: <MemorySettingsRoute />, ErrorBoundary: RouteErrorBoundary },
    ],
  },
  {
    path: '/onboarding',
    element: <Navigate to="/onboarding/step-1" replace />,
    ErrorBoundary: RouteErrorBoundary,
  },
  { path: '/onboarding/:step', element: <OnboardingRoute />, ErrorBoundary: RouteErrorBoundary },
  { path: '/bli-med/:token', element: <MemberInviteRoute />, ErrorBoundary: RouteErrorBoundary },
  // #141: admin-utstedt engangslenke for nytt passord. Uautentisert, som /bli-med.
  { path: '/nytt-passord/:token', element: <PasswordResetRoute />, ErrorBoundary: RouteErrorBoundary },
  { path: '/login', element: <LoginRoute />, ErrorBoundary: RouteErrorBoundary },
  // FF-09 (#177): norsk adresse for lenken systemadmin gir videre, `/logg-inn?familie=<kode>`.
  { path: '/logg-inn', element: <LoginRoute />, ErrorBoundary: RouteErrorBoundary },
  // #178: systemadmin (epic #167 D3/D8) — utenfor familieappens skall, egen
  // innlogging med passord og TOTP. Ingen familienavigasjon, ingen familiedata.
  { path: SYSTEM_LOGIN_PATH, element: <SystemLoginRoute />, ErrorBoundary: RouteErrorBoundary },
  { path: SYSTEM_HOME_PATH, element: <SystemShellRoute />, ErrorBoundary: RouteErrorBoundary },
  // #143: utenfor skallet — ingen navigasjon før eget passord er valgt.
  { path: CHOOSE_PASSWORD_PATH, element: <ChooseOwnPasswordRoute />, ErrorBoundary: RouteErrorBoundary },
  // Phase 6 D-05: /installer is the iOS install walkthrough — unauthenticated
  // by design. RR7 SPA routes have no middleware concept and the SPA does not
  // gate routes server-side, so the route is already public; this comment
  // documents the intent for future readers.
  { path: '/installer', element: <InstallRoute />, ErrorBoundary: RouteErrorBoundary },
  // Plan 06-09 Task 3: /dev/state-primitives is a test-only gallery
  // (ErrorState / LoadingState / EmptyState in all six variants) used by
  // apps/web/e2e/visual/state-primitives.spec.ts to capture per-surface
  // baselines. #152 adds /dev/primitives, the gallery of every primitive, for
  // primitives-gallery.spec.ts. Both are mounted ONLY when DEV_GALLERY is true
  // so Vite tree-shakes the lazy imports out of production builds.
  ...(DEV_GALLERY
    ? [
        {
          path: '/dev/state-primitives',
          // Lazy import keeps the dev surface out of the production bundle
          // entirely — Rolldown drops the chunk when DEV_GALLERY is false.
          lazy: async () => {
            const mod = await import('./dev/state-primitives-route');
            return { Component: mod.StatePrimitivesRoute };
          },
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/dev/primitives/*',
          lazy: async () => {
            const mod = await import('./dev/primitives-gallery-route');
            return { Component: mod.PrimitivesGalleryRoute };
          },
          ErrorBoundary: RouteErrorBoundary,
        },
      ]
    : []),
  { path: '*', element: <NotFoundRoute />, ErrorBoundary: RouteErrorBoundary },
]);

// v1.1 tech-debt closeout: mount the calendar + points WS consumers app-wide
// (not per-route like useChoresWS) so the always-on kitchen tablet dashboard
// and every phone reflect calendar edits and point/reward changes in real
// time. invalidateQueries only refetches currently-mounted queries, so a
// global subscription stays cheap at family scale. This MUST render inside
// DashboardQueryProvider — both hooks call useQueryClient().
function RealtimeSync() {
  useCalendarWS();
  usePointsWS();
  // Fase 5: keep the family feed live wherever the user is (item_added +
  // reaction_changed → invalidate ['feed']). Cheap — only refetches when the
  // feed query is actually mounted.
  useFeedWS();
  // Wave 1 (v1.1): keep the dashboard HomeStatusStrip live when the light
  // tally changes. Cheap — only refetches the status query when it's mounted.
  useSmartHomeWS();
  return null;
}

export function App() {
  useSwUpdate();
  useSystemReloadWS();
  // Plan 04-04 D-09 (Pitfall 2 — iOS Background Sync absent): wake the
  // shopping-mutations-v1 queue from the page on visibilitychange + online.
  // The SW message handler in apps/web/src/sw.ts catches `DRAIN_QUEUE` and
  // calls `_queue.replayRequests()` so queued offline writes flush even when
  // the browser doesn't fire the standard `sync` event (e.g. iOS Safari).
  useEffect(() => registerShoppingDrainTriggers(), []);
  return (
    // AppErrorBoundary sits OUTSIDE DashboardQueryProvider so a crash in
    // the provider's own mount (e.g. QueryClient construction) is still
    // caught — see Plan 06-02 Task 1 + UI-SPEC D-15. SwUpdateBanner +
    // SwIdleReload stay INSIDE the boundary: when the rest of the app is
    // dead, the banner is useless, and keeping them inside means the
    // boundary can render its own page-variant ErrorState as the sole
    // tree content.
    <>
      {/* #151: lerretet med glød — ett fast lag for hele appen, skallet og
          flytene utenfor det. Et element og ikke `background-attachment:
          fixed`, som iOS Safari ignorerer. Står utenfor feilgrensen, så også
          feilsiden får lerretet. Se @utility paper-tex i theme.css. */}
      <div aria-hidden="true" className="paper-tex pointer-events-none fixed inset-0 -z-10" />
      <AppErrorBoundary>
        <DashboardQueryProvider>
          <RealtimeSync />
          {/* #151: mørkt tema gjelder hele appen, også flytene utenfor skallet
              (/login, /onboarding, /bli-med …). DuskTheme satt tidligere inne i
              IdleMode og nådde bare rutene under DashboardRoute. Begge spørringene
              den gjør (/api/sun/today, /api/app-config/theme-mode) er uten
              innlogging, så den kan stå her før noen er logget inn. */}
          <DuskTheme />
          {/* #155: «Se alle merkene mine» på merke-feiringen navigerer via
              ruteren, som står inne i leverandøren. */}
          <CelebrationProvider navigate={(to) => void router.navigate(to)}>
            <RouterProvider router={router} />
            <SwUpdateBanner />
            <SwIdleReload />
            {/* Issue #56: the single renderer for lib/toast. Must stay OUTSIDE
                InteractiveMode, because InteractiveMode is route-scoped — only
                <DashboardRoute/> renders it, so /login, /onboarding,
                /bli-med/:token, /installer and the 404 route all sit outside it
                and a viewport mounted inside would put toasts on some surfaces
                and not others. (It would also inherit InteractiveMode's
                `opacity-0 pointer-events-none` idle fade, though that alone is
                not decisive: the IdleMode overlay is `fixed inset-0 z-60` with
                an opaque `paper-tex`, so it occludes this z-40 toast anyway.) */}
            <ToastViewport />
          </CelebrationProvider>
        </DashboardQueryProvider>
      </AppErrorBoundary>
    </>
  );
}
